<script>
    import { onMount } from 'svelte';

    import { lang } from './language';
    import { mods, events, reload, disabled } from './mods/modules';
    import { baseData } from './helpers/data';

    import { calculateAge } from './helpers/calculateAge';
    import { getDateMin, getDay, getMonthNumber, getFullYear } from './helpers/date';
    import { configCommunities } from './mods/configCommunities';
    import { countries } from './countries';
    import { nextButtonVisibility } from './mods/nextButtonVisibility';

    import CTA from './components/cta.svelte';
    import GENDER from './components/gender.svelte';
    import BIRTHDATE from './components/birthdate.svelte';
    import FIRSTNAME from './components/name.svelte';
    import COUNTRY from './components/country.svelte';
    import EMAIL from './components/emailConsent.svelte';
    import UPLOAD from './components/upload.svelte';
    import AJAX from './components/ajax.svelte';
    import VIGNETTE from './components/vignette.svelte';
    import P2DYN from './components/p2Dynamic.svelte';

    import './global.css';

    //REGION CONSTANTS
    const __CONFIG = 'multistep-config';

    //REGION FUNCTIONS
    // Prevent back button behavior
    window.history.pushState({ page: 0 }, '', '');
    window.onpopstate = function (event) {
        window.history.pushState({ page: store.nav.current }, '', '');
        event.stopPropagation();
        event.preventDefault();
        backintime(store.nav.current - 1);
    };

    /**
     * return the widget type for a step
     * @param step
     * @param data
     */
    function getType(step, data) {
        return mods[data.steps[step]?.type];
    }

    /**
     * get i18n value
     */
    function getI18n(data) {
        return !!data.global?.options?.community ? data.global.options.community : 'en';
    }

    function getMultistepConfig() {
        Maestro.getInstance()
            .Store.loadAsync(__CONFIG)
            .then((config) => {
                if (Object.keys(config).length) data = config;
                const i18n = getI18n(data);
                const geo_country = document.querySelector('[data-emi-country]')?.getAttribute('data-emi-country');
                if (i18n !== store.i18n && store.country?.iso === configCommunities[store.i18n].country && !geo_country) {
                    // edge case: if i18n has changed and we used default country for this community, reset country
                    store.country = {};
                }
                store.i18n = i18n;
                if (!store.country?.iso) {
                    // when config is received, the country selected is the first one of the top country from the top countries
                    // map to build full country object based on the iso code from topCountry and using countries from country variable
                    store.country = configCommunities[store.i18n].topCountries.map(
                        (iso) => countries.find((country) => country.iso === iso)
                        // take the first one (index 0)
                    )[0];
                }
                store.modal = data.modal;
                store.progressBar = data.progressBar;
                store.backgroundColor = data.backgroundColor;
                store.opacity = data.opacity;
                store.nav.steps.total = data.steps.filter( step => !disabled[step.type] && step.type != 'ajax' ).length;
                stepResolver(0);
            });
    }

    function setEventListener(step, data) {
        // EVENT LISTENERS ON EACH WIDGET SELECTION
        Maestro.getInstance().EventBus.on(events[getType(step, data)], function () {
            Maestro.getInstance().EventBus.off(events[getType(step, data)]);
            Maestro.getInstance().EventBus.emit(`${getType(step, data)}-stop`);
            stepResolver(step + 1);
        });
    }

    function emitEvent(event, data) {
        Maestro.getInstance().EventBus.emit(event, data);
        Maestro.getInstance().Store.save(event, data);
    }

    function stopModuleOnLastStep(data, step) {
        if (data.steps.length < step + 1) {
            emitEvent(`${getType(step + 1, data)}-stop`);
        }
    }

    function getWidgetParams(data, step) {
        return reload.includes(mods[data.steps[step].type])
            ? JSON.parse(JSON.stringify(data.steps[step].config))
            : undefined;
    }

    function startNotDisabledWidgets(data, step) {
        // !data.steps[step].disabled -- step is disabled when user action has been made ex: drawn a card
        // if is a widget && is not disabled
        if (mods[data.steps[step].type] && !data.steps[step].disabled) {
            emitEvent(`${mods[data.steps[step].type]}-start`, getWidgetParams(data, step));
            findWidgetsElements(show);
        } // resume module (and send new config if available)
    }

    function launchAjax(step, store) {
        //FORMAT OPTIONS OBJECT
        const options = {
            birthdate: new Date(store.birthdate),
            brandConsent: store.brandConsent,
            community: store.i18n,
            country: store.country,
            email: store.email,
            gender: store.gender,
            name: store.firstname,
            partnerOptin: store.optin,
            secondBrand: store.secondBrandConsent, // second brand consent
            p2DynBrand: store.p2.brand, // p2 dynamic brand if defined
            vignette: Object.values(store.vignette)
        };

        // LAUNCH THE CONFIG FUNCTION GIVEN BY THE DATA
        data.steps[step].config.ajax(JSON.parse(JSON.stringify(options))).then(() => {
            console.log('ok');
        });
    }

    function goToPreviousStepIfDisabledStep(data, step, store) {
        if (step != 0 && disabled[data.steps[store.nav.current - 1].type]) {
            data.steps[store.nav.current - 1].disabled = true;
        }
    }

    function goToNextStepIfDisabledStep(data, step, store) {
        if (data.steps[step].disabled) {
            stepResolver(store.nav.current + 1);
        } // Continue to the next step if the current step is disabled
    }

    // FUCTION FOR THE STEP NAVIGATION
    function stepResolver(step) {
        store.step = data.steps[step];

        // stop module on last step
        stopModuleOnLastStep(data, step);

        // move to the new step
        store.nav.current = step;

        // update Step progression indicator
        stepIndicator(data);

        //launch start event when on a not disabled widget step
        startNotDisabledWidgets(data, step);

        if (data.steps[step].type === 'ajax') {
            launchAjax(step, store);
        } // start registration process

        goToPreviousStepIfDisabledStep(data, step, store);
        goToNextStepIfDisabledStep(data, step, store);

        setEventListener(store.nav.current, data);
        if (mods[store.step.type] || store.step.type === 'email-consent') {
            store.isModule = 'module';
        } else {
            store.isModule = 'step';
        }

        if (store.step.type === 'email-consent') {
            const elt = document.querySelector('#app-emi-multistep');
            const scroll = elt.getBoundingClientRect().top;

            setTimeout(function () {
                const tarot = document.querySelector('#app-emi-tarot');
                if (tarot) {
                  scrollTo(0, tarot.getBoundingClientRect().top);
                }
            }, 250);

            setTimeout(function () {
                scrollTo(0, scroll);
            }, 3000);
        }
    }

    // FUNCTION BACK
    function backintime(step) {
        if (!data.steps[step]) {
            return false;
        }
        if (data.steps[step].disabled) {
            return backintime(step - 1);
        }
        store.error = false;
        stepResolver(step);
    } // Go back to the previous enabled step

    // Called when the step is in "completed" state
    function complete(step, index) {
        if (store.step.hideCta) {
            // If the CTA button is hidden for the current step, then, validate the step
            validate(step.type, index);
        }
    }

    // Set the step progression indicator
    function stepIndicator(data) {
        // get only path before current step and filter "disabled" and ajax steps
        const path = data.steps.slice(0, store.nav.current + 1).filter(step => !disabled[step.type] && step.type !== 'ajax');
        // find last element in path with same type as current step
        for (let i = path.length - 1; i >= 0; i--) {
            if (path[i].type === data.steps[store.nav.current].type) {
                const progress = i + 1;
                store.nav.steps.path[progress - 1] = store.step.type;
                store.nav.steps.path = store.nav.steps.path.slice(0, progress);
                break;
            }
        }
    }

    /**
     * @description validation function for the birthdate fields
     */
    function validateBirthdate() {
        const birthdate = parseBirthdate();
        // valid data
        if (isUserAdult(birthdate)) {
            onUserAdult();
        }
        // invalid because birthdate is not complete
        if (isBirthdateIncomplete(birthdate)) {
            onIncompleteBirthdate();
        }
        // invalid because user < 18
        if (isUserMinor(birthdate)) {
            onUserMinor();
        }
    }

    /**
     * @description parse the birthdate using the string from the global variable store
     * @returns {Number}
     */
    function parseBirthdate() {
        return Date.parse(dateString(store));
    }

    /**
     * @description date from the store as a string yyyy-mm-dd
     */
    function dateString() {
        return store.birth.year && store.birth.month && store.birth.day ? `${store.birth.year}-${store.birth.month}-${store.birth.day}` : '';
    }

    function isUserAdult(birthdate) {
        return !isNaN(birthdate) && calculateAge(birthdate) >= 18;
    }

    function onUserAdult() {
        store.birthdate = dateString(store);
        store.error = false;
        store.errors.day = false;
        store.errors.month = false;
        store.errors.year = false;
        stepResolver(++store.nav.current);
    }

    function isBirthdateIncomplete(birthdate) {
        return isNaN(calculateAge(birthdate));
    }

    function onIncompleteBirthdate() {
        store.error = lang[store.i18n].incompleteBirthdate;
        store.errors.day = store.birth.day === '' ? true : false;
        store.errors.month = store.birth.month === '' ? true : false;
        store.errors.year = store.birth.year === '' ? true : false;
    }

    function isUserMinor(birthdate) {
        return calculateAge(birthdate) < 18;
    }

    function onUserMinor() {
        store.error = data.steps[store.nav.current]?.config.form.label.tooYoungError;
    }
    // VALIDATION OF STEPS
    function validate(type, index) {
        switch (type) {
            case 'birth-date':
                validateBirthdate(store);
                break;
            case 'gender':
                if (store.gender) {
                    stepResolver(++store.nav.current);
                    store.error = false;
                    store.errors.gender = false;
                } else {
                    store.error = lang[store.i18n].genderError;
                    store.errors.gender = true;
                }
                break;
            case 'name':
                if (store.firstname && store.firstname.trim().length > 0) {
                    stepResolver(++store.nav.current);
                    store.error = false;
                    store.errors.firstname = false;
                } else {
                    store.error = lang[store.i18n].firstnameError;
                    store.errors.firstname = true;
                }
                break;
            case 'country':
                if (store.country) {
                    store.error = false;
                    stepResolver(++store.nav.current);
                }
                break;
            case 'email-consent':
                const regex = /^([a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)$/;
                if (store.brandConsent && store.email && store.email.match(regex)) {
                    store.error = false;
                    const p2Dyn = [true, 'true'].includes(data.steps[store.nav.current]?.config?.form?.p2Dyn);
                    const p2DynNeutral = [true, 'true'].includes(data.steps[store.nav.current]?.config?.form?.p2DynNeutral);
                    store.errors.email = false;
                    store.errors.emailConsent = false;
                    sessionStorage.setItem('email', store.email);
                    stepResolver(++store.nav.current);

                    if (p2Dyn || p2DynNeutral) {
                        store.p2.loading = true;
                        if (typeof getP2 === 'function') {
                            getP2(store.email, p2DynNeutral)
                                .then((r) => {
                                    if (r.status === 200) {
                                        r.json().then(body => {
                                            store.p2.loading = false;
                                            store.p2.brand = body.target;
                                            store.p2.content = body.phrase;
                                        });
                                    } else {
                                        // skip P2 step if we have another status code
                                        stepResolver(++store.nav.current);
                                    }
                                })
                                .catch((e) => {
                                    // skip P2 step if there is an error
                                    stepResolver(++store.nav.current);
                                });
                        } else {
                            // skip P2 step (getP2 function not available)
                            stepResolver(++store.nav.current);
                        }
                    }
                } else {
                    store.error = '';
                    if (!store.brandConsent) {
                        store.error += lang[store.i18n].consentError + '<br/>';
                        store.errors.emailConsent = true;
                    }
                    if (!store.email || !store.email.match(regex)) {
                        store.error += store.error = lang[store.i18n].emailError + '<br/>';
                        store.errors.email = true;
                    }
                }
                break;
            case 'vignette':
                const choices = data.steps[store.nav.current]?.choices || 1;
                if (store.vignette[index] && store.vignette[index].length === choices) {
                    store.error = false;
                    stepResolver(++store.nav.current);
                } else {
                    store.error = lang[store.i18n][choices > 1 ? 'choicesErrorPlural' : 'choicesErrorSingular'].replace(
                        '{{choices}}',
                        choices
                    );
                }
                break;
            case 'cta':
                const uploadFile = data.steps[store.nav.current]?.config?.uploadFile;
                if ((uploadFile && store.upload.image) || !uploadFile) stepResolver(++store.nav.current);
                break;
            case 'p2-dyn':
                if (store.p2.brand) store.secondBrandConsent = true;
                stepResolver(++store.nav.current);
                break;
            default:
                store.error = false;
                stepResolver(++store.nav.current);
        }
    }

    //ENREGION FUNCTIONS

    // BUTTON VISIBILITY - Steps on which it is displayed
    const next = nextButtonVisibility;

    //Base data initializing
    let data = baseData;

    // CALCULATION OF THE BIRTH DATE MINIMAL DATE
    const dateMin = getDateMin();

    let day = getDay(dateMin);
    let month = getMonthNumber(dateMin);
    let yearI = getFullYear(dateMin);

    // STORE INITIAL OBJECT -- WILL BE REPLACE BY THE CONFIG
    let store = {
        nav: {
            current: 0,
            back: false,
            steps: {
                total: 1,
                path: []
            }
        },
        i18n: getI18n(data),
        gender: '',
        birthdate: '',
        birth: {
            day: '',
            month: '',
            year: ''
        },
        country: {},
        firstname: '',
        email: '',
        optin: false,
        brandConsent: false,
        secondBrandConsent: false,
        vignette: {},
        p2: {
            loading: false,
            brand: '',
            content: ''
        },
        upload: {
            image: ''
        },
        modal: false,
        progressBar: false,
        opacity: 1,
        backgroundColor: '#000000',
        errors: {
            day: false,
            month: false,
            year: false,
            gender: false,
            firstname: false,
            email: false,
            emailConsent: false
        }
    }; // empty store

    // Load data with maestro
    onMount(async () => {
        getMultistepConfig();
    });

    // BANNER FUNCTIONNALITY
    const hasBanner = typeof data.global.banner !== 'undefined' && typeof data.global.banner?.url === 'string';
    const bannerSrc = data.global.banner?.url;
    const bannerAlt = data.global.banner?.alt;

    //FUNCTION FOR CHANGES
    const changeGender = (event) => {
        store.gender = event.detail;
    };
    // ACTIONS ON DATE CHANGES
    const changeDay = (event) => {
        store.birth.day = event.detail;
    };
    // ACTIONS ON MONTH CHANGES
    const changeMonth = (event) => {
        store.birth.month = event.detail;
    };
    // ACTIONS ON YEAR CHANGES
    const changeYear = (event) => {
        store.birth.year = event.detail;
    };
    // ACTIONS ON NAME CHANGES
    const changeName = (event) => {
        store.firstname = event.detail;
    };
    // ACTIONS ON COUNTRY CHANGES
    const changeCountry = (event) => {
        store.country = event.detail;
    };
    // ACTIONS ON EMAIL CHANGES
    const changeEmail = (event) => {
        store.email = event.detail;
    };
    // ACTIONS ON BRAND CONSENT CHANGES
    const changeBrandConsent = (event) => {
        store.brandConsent = event.detail;
    };
    // ACTIONS ON SECOND BRAND CHANGES
    const changeSecondBrandConsent = (event) => {
        store.secondBrandConsent = event.detail;
    };
    // ACTIONS ON PARTNER OPTIN CHANGES
    const changePartnerOptin = (event) => {
        store.optin = event.detail;
    };
    // ACTIONS ON VIGNETTE CHANGES
    const changeVignette = (event, i) => {
        store.vignette[i] = event.detail;
    };
    //const i18n = !!data.global?.options?.community == true ? data.global.options.community : 'en';

    const geo_country = document.querySelector('[data-emi-country]')?.getAttribute('data-emi-country');
    for (let country of countries) {
        if (geo_country) { // Use data-emi-country attribute to get country from geolocation
            if (country.iso3 == geo_country) {
                store.country = country;
            }
        } else if (country.iso == configCommunities[store.i18n].country) {
            store.country = country;
        }
    }

    function hexToRgb(hex, alpha) {
        hex = hex.replace('#', '');
        var r = parseInt(hex.length === 3 ? hex.slice(0, 1).repeat(2) : hex.slice(0, 2), 16);
        var g = parseInt(hex.length === 3 ? hex.slice(1, 2).repeat(2) : hex.slice(2, 4), 16);
        var b = parseInt(hex.length === 3 ? hex.slice(2, 3).repeat(2) : hex.slice(4, 6), 16);
        if (alpha) {
            return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
        } else {
            return 'rgb(' + r + ', ' + g + ', ' + b + ')';
        }
    }

    function modalStyle() {
        return store.modal && store.backgroundColor
            ? 'background:'.concat(
                hexToRgb(store.backgroundColor, store.opacity ? store.opacity : '.9').concat(' !important;')
            )
            : '';
    }

    function findWidgetsElements(actionFct) {
        const arr = Object.keys(mods);

        for (let i = 0; i < arr.length; i++) {
            let elt = document.querySelector('#app-emi-'.concat(mods[arr[i]]));

            if (elt) {
                actionFct(elt);
            }
        }
    }

    function hide(elt) {
        elt.classList.add('emi-d-none');
    }

    function show(elt) {
        elt.classList.remove('emi-d-none');
    }

    findWidgetsElements(hide);
</script>

<!-- ======================== HTML TEMPLATE ================================= -->
<div
    class="app-msi_container"
    data-step={store.isModule}
    data-modal={store.modal ? 'yes' : 'no'}
    style={store.isModule === 'step' ? modalStyle() : ''}
>
    <div class="app-msi_flex-column emi-modal">
        <!-- IF THE CONFIG CONTAINS A BANNER CONFIGURATION AND A BANNER URL, DISPLAY THIS -->
        {#if hasBanner}
            <div class="app-msi_banner-container">
                <img src={bannerSrc} alt={bannerAlt} class="app-msi_banner-img" />
            </div>
        {/if}

        <!-- {lang[store.i18n].step} {store.nav.steps.path.length || 1} / {store.nav.steps.total}
        <meter max="{store.nav.steps.total}" value="{store.nav.steps.path.length || 1}"></meter> -->

        <!-- Step progress indicator -->
        {#if store.progressBar && (!store.isModule || !mods[store.step?.type]) }
            <div class="progress">
                <!-- <span>{lang[store.i18n].step} {store.nav.steps.path.length || 1} / {store.nav.steps.total}</span> -->
                <div class="progress_bar" style="width: {((store.nav.steps.path.length - 1) / (store.nav.steps.total - 1)) * 100 + "%"}"></div>
                <ul class="">
                    {#each Array(store.nav.steps.total) as _, i }
                        <li
                        on:click={() => {
                            const stepType = store.nav.steps.path[i];
                            // if there are multiple same step type in path before clicked position, get wanted occurence
                            const stepTypeOccurence = store.nav.steps.path.slice(0, i + 1).filter(type => type === stepType).length - 1;
                            // get indexes for this step type and select occurence
                            const step = data.steps
                            .map((step, index) => step.type === stepType ? index : null)
                            .filter(step => step !== null)
                            [stepTypeOccurence];

                            if (step !== undefined) stepResolver(step);
                        }}
                        class={i <= store.nav.steps.path.length - 1 ? 'active' : ''}>{i + 1}</li>
                    {/each}
                </ul>
            </div>
        {/if}

        <!-- IF THE CONFIG CONTAINS A BANNER CONFIGURATION AND A BANNER URL, DISPLAY THIS -->
        <!-- TITLE OF THE PAGE -->
        <div class="app-msi_text-center app-msi_title">
            {#each data.steps as step, i}
                {#if i == store.nav.current && !mods[step.type] && next[step.type] && (store.nav.current >= 2 || (store.nav.current == 1 && !mods[data.steps[0].type]))}
                    <span
                        class="emi-back-arrow"
                        on:click={() => {
                            backintime(store.nav.current - 1);
                        }}
                    >
                        &larr;
                    </span>
                {/if}
            {/each}
            {data.steps[store.nav.current].title}
        </div>
        <div>
            <ul>
                <!-- LOOP ON EACH STEP -->
                {#each data.steps as step, i}
                    <!-- IF THE STEP IS THE CURRENT ONE, DISPLAY THIS -->
                    {#if i == store.nav.current}
                        <li>
                            <!-- <svelte:component this={step.type}/> -->

                            {#if store.error}
                                <p class="error">{@html store.error}</p>
                            {/if}
                            <!-- CTA COMPONENT -->
                            {#if step.type === 'cta'}
                                <CTA htmlContent={step.config?.content} />
                                {#if step.config?.uploadFile}
                                    <UPLOAD
                                        skipUploadText={step.config?.skipText}
                                        on:image={(image) => (store.upload.image = image.detail)}
                                        on:skip={() => stepResolver(++store.nav.current)}
                                    />
                                {/if}
                            {/if}
                            <!-- GENDER COMPONENT -->
                            {#if step.type === 'gender'}
                                <GENDER
                                    maleLabel={data.form?.label?.male || lang[store.i18n].male}
                                    femaleLabel={data.form?.label?.female || lang[store.i18n].female}
                                    value={store.gender}
                                    error={store.errors.gender}
                                    on:gender={changeGender}
                                />
                            {/if}
                            <!-- BIRTH DATE COMPONENT -->
                            {#if step.type === 'birth-date'}
                                <BIRTHDATE
                                    dayLabel={data.form?.label?.day || lang[store.i18n].day}
                                    monthLabel={data.form?.label?.day || lang[store.i18n].month}
                                    yearLabel={data.form?.label?.day || lang[store.i18n].year}
                                    i18n={store.i18n}
                                    day={store.birth.day}
                                    month={store.birth.month}
                                    year={store.birth.year}
                                    errors={store.errors}
                                    on:day={changeDay}
                                    on:month={changeMonth}
                                    on:year={changeYear}
                                />
                            {/if}
                            <!-- FIRSTNAME COMPONENT -->
                            {#if step.type === 'name'}
                                <FIRSTNAME
                                    message={data.form?.label?.message || ''}
                                    placeholder={lang[store.i18n].name || data.form?.label?.field}
                                    value={store.firstname}
                                    error={store.errors.firstname}
                                    on:name={changeName}
                                />
                            {/if}
                            <!-- COUNTRY COMPONENT -->
                            {#if step.type === 'country'}
                                <COUNTRY i18n={store.i18n} value={store.country.iso} on:country={changeCountry} />
                            {/if}
                            <!-- EMAIL CONSENT COMPONENT -->
                            {#if step.type === 'email-consent'}
                                <EMAIL
                                    placeholder={step.config.form?.label?.emailField || lang[store.i18n].emailField}
                                    brandLabel={step.config.form?.label?.brandConsentMessage}
                                    brandAgreementMessage={step.config.form?.label?.brandAgreementMessage}
                                    secondBrandTitle={step.config?.form?.label?.secondBrandTitle}
                                    secondBrandMessage={step.config?.form?.label?.secondBrandMessage}
                                    secondBrandAgreementMessage={step.config.form?.label?.secondBrandAgreementMessage}
                                    partnerBrandTitle={step.config?.form?.label?.partnerBrandTitle}
                                    partnerBrandMessage={step.config?.form?.label?.partnerBrandMessage}
                                    partnerBrandAgreementMessage={step.config.form?.label?.partnerBrandAgreementMessage}
                                    p2Dyn={[true, 'true'].includes(step.config.form?.p2Dyn)}
                                    p2DynNeutral={[true, 'true'].includes(step.config.form?.p2DynNeutral)}
                                    error={store.errors.email}
                                    consentError={store.errors.emailConsent}
                                    on:email={(event) => {
                                        changeEmail(event);
                                    }}
                                    on:brandConsent={(event) => {
                                        changeBrandConsent(event);
                                    }}
                                    on:secondBrandConsent={(event) => {
                                        changeSecondBrandConsent(event);
                                    }}
                                    on:partnerOptin={(event) => {
                                        changePartnerOptin(event);
                                    }}
                                />
                            {/if}
                            <!-- VIGNETTE COMPONENT -->
                            {#if step.type === 'vignette'}
                                <VIGNETTE
                                    community={store.i18n}
                                    openingText={step.openingText}
                                    resultText={step.resultText}
                                    direction={step.direction}
                                    horizontalAlign={step.horizontalAlign}
                                    items={step.items}
                                    values={store.vignette[i]}
                                    backgroundColor={step.backgroundColor}
                                    borderRadius={step.borderRadius}
                                    choices={step.choices}
                                    on:vignette={(event) => changeVignette(event, i)}
                                    on:complete={() => complete(step, i)}
                                />
                            {/if}
                            <!-- UPLOAD COMPONENT -->
                            {#if step.type === 'upload'}
                                <UPLOAD on:image={(image) => (store.upload.image = image.detail)} />
                            {/if}
                            <!-- P2 DYNAMIC COMPONENT -->
                            {#if step.type === 'p2-dyn'}
                                <P2DYN
                                    interestedText={data.global.label.p2DynSubmit}
                                    notInterestedText={step.notInterestedText}
                                    content={store.p2.content}
                                    loading={store.p2.loading}
                                    i18n={store.i18n}
                                    on:accept={() => validate(step.type, i)}
                                    on:refuse={() => {
                                        stepResolver(++store.nav.current)
                                    }}
                                />
                            {/if}
                            <!-- AJAX COMPONENT -->
                            {#if step.type === 'ajax'}
                                <AJAX
                                    message={step.config.form?.label?.message}
                                    loading="true"
                                    success="false"
                                    successLabel={step.config.validation?.label?.success}
                                    failLabel={step.config.validation?.label?.failed}
                                />
                            {/if}
                            <!-- BUTTON COMPONENT -->
                            {#if
                                !mods[step.type] &&
                                next[step.type] &&
                                !store.step.hideCta &&
                                ((step.type === 'cta' && step.config.uploadFile && store.upload.image) || (step.type === 'cta' && !step.config.uploadFile) || step.type !== 'cta')
                            }
                                <div class="app-msi_actions">
                                    <button
                                        on:click={() => validate(step.type, i)}
                                        class="app-msi_btn app-msi_btn-primary"
                                        type="submit"
                                    >
                                        <!-- if step is email, the submit label should appear, else the next label is appearing -->
                                        {#if step.type !== 'email-consent'}
                                            {step.next ? step.next : data.global.label.next}
                                        {/if}
                                        {#if step.type === 'email-consent'}
                                            {step.next ? step.next : data.global.label.submit}
                                        {/if}
                                    </button>
                                </div>
                            {/if}
                        </li>
                    {/if}
                {/each}
            </ul>
        </div>
    </div>
</div>

<!-- FOR TESTING PURPOSE ONLY -- DISPLAY A WAY TO NAVIGATE BETWEEN STEPS WITHOUT USING THE FULL FORM -->
<!--
{#if store.nav.current >= 2 || (store.nav.current == 1 && !mods[data.steps[0].type])}
    <button
        on:click={() => {
            backintime(store.nav.current - 1);
        }}>ᐊ</button
    >
{/if}

<select bind:value={store.nav.current} on:change={() => stepResolver(store.nav.current)}>
    {#each data.steps as step, i}
        <option value={i}>{step.type}</option>
    {/each}
</select>

<pre><code>{JSON.stringify({ store }, null, 4)}</code></pre>

<style>
    code {
        display: block;
        margin: 50px;
    }
</style>
-->
